// PlatformOverridesShell — generic per-event override editor. The Universe /
// UrVenue / … override pages are thin wrappers that supply:
//
//   - platform (route segment, e.g. 'universe', 'urvenue')
//   - platformLabel (display name)
//   - overridesKey (which event field carries the overrides JSONB)
//   - fieldCatalog ([{ key, group, label, kind }]) — what's overridable
//   - inheritedValue (resolved, key) => value — platform-specific lookup
//     into the resolver's payload shape
//
// The editor is "edit-the-diff": it renders the WHOLE catalog as a real form
// pre-filled with the resolved (inherited) values, each field with a real
// widget (toggle / number / rate-ladder / image picker / …) and an
// inheritance badge. Editing a field pins it as an event-level override;
// editing a not-yet-pinned field back to its inherited value is a no-op (we
// don't pin it). Per-field "reset" drops the override. Ops never picks fields
// from a catalog and never edits JSON (except the explicit raw fallback).
//
// Backend contract (unchanged — same routes as before):
//   GET    /api/tenants/:slug/{platform}/events/:id/preview      → { payload, sources, kind }
//   PATCH  /api/tenants/:slug/{platform}/events/:id/overrides    body: { [key]: value | null }
//   GET    /api/tenants/:slug/{platform}/events/:id/drift        → { has_drift, deltas[], listing_id }
//   POST   /api/tenants/:slug/{platform}/events/:id/push-field   body: { field }
//   POST   /api/tenants/:slug/{platform}/events/:id/accept-field body: { field }

import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useParams, Link } from 'react-router-dom';
import { OverrideField, FIELD_RENDERED_ELSEWHERE, SourceLegend } from './fieldEditors.jsx';
import { LoadingState } from '../../lib/Spinner.jsx';
import { formatDateLong } from '../../lib/events.jsx';
import { formatStamp } from '../../lib/timeFields.jsx';

function jsonEq(a, b) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

// Split a tab's fields into ordered sub-sections by their `section` (preserving
// first-appearance order); fields without a `section` collapse into one
// unlabeled group.
function groupBySections(fields) {
  const order = [];
  const by = new Map();
  for (const f of fields) {
    const s = f.section || null;
    if (!by.has(s)) { by.set(s, []); order.push(s); }
    by.get(s).push(f);
  }
  return order.map((section) => ({ section, items: by.get(section) }));
}

function timeAgo(iso) {
  if (!iso) return null;
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function PlatformOverridesShell({
  platform,
  platformLabel,
  overridesKey,
  fieldCatalog,
  inheritedValue,
  // Optional: maps a catalog key → the resolver's `sources` key when they
  // differ (e.g. UrVenue's 'event_type_id' → 'eventtypeid'). Identity by
  // default; only affects the inherited-source badge, not override detection.
  sourceKey = (k) => k,
  // Optional: explicit tab order (e.g. Universe's real editor section order).
  // When absent, falls back to "Tickets first, then catalog order".
  groupOrder = null,
  // When true (Universe), saving a field ALSO pushes it live via the
  // platform's save-field endpoint, with an optimistic-concurrency collision
  // check against the live listing. Platforms without that endpoint keep the
  // local-only PATCH /overrides behavior.
  pushOnSave = false,
  // When true, an Overview tab (status + live snapshot + customized fields +
  // quick actions) is prepended and used as the default landing — mirrors
  // Universe's overview-first host editor.
  overviewTab = false,
  // When true (Universe/UrVenue), each promotable field row gets "→ venue" /
  // "→ template" buttons that save its value up an inheritance tier via the
  // platform's promote-field endpoint.
  promoteFields = false,
  // Optional per-platform promotable key lists {venue:[], template:[]}; the
  // field layer falls back to the Universe sets when absent.
  promotable = null,
}) {
  const { tenantSlug, eventId } = useParams();
  const [resolved, setResolved] = useState(null);
  const [event, setEvent] = useState(null);
  const [live, setLive] = useState(null);       // live platform state (null if unpublished/unavailable)
  const [drift, setDrift] = useState(null);
  const [driftLoading, setDriftLoading] = useState(false);
  const [showFullPayload, setShowFullPayload] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [pushNote, setPushNote] = useState(null);   // { key, status: 'pushed'|'local'|'failed', detail }
  const [conflict, setConflict] = useState(null);   // { key, ours, live }, 409 from save-field

  const load = async () => {
    setError(null);
    try {
      const [pr, ev, lv] = await Promise.all([
        apiFetch(`/api/tenants/${tenantSlug}/${platform}/events/${eventId}/preview`).then((r) => r.json()),
        apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}`).then((r) => r.json()),
        // Live state is best-effort: unpublished events / platforms without a
        // /live route just leave the live panel hidden. Never blocks the editor.
        apiFetch(`/api/tenants/${tenantSlug}/${platform}/events/${eventId}/live`)
          .then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ]);
      if (pr.error) throw new Error(pr.detail || pr.error);
      setResolved(pr);
      setEvent(ev);
      setLive(lv && lv.published ? lv : null);
    } catch (e) {
      setError(e.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantSlug, eventId, platform]);

  // Overview-first editors auto-check drift once the live state is known, so
  // the Overview's sync indicator is accurate without a manual click.
  useEffect(() => {
    if (overviewTab && live && !drift && !driftLoading) checkDrift();
    /* eslint-disable-next-line */
  }, [overviewTab, live]);

  // Deep-link from the event overview ("Resolve drift →" → …/universe#drift):
  // once drift is loaded and there is drift, scroll the resolve panel into view
  // and flash it, so the link lands on the controls instead of the page top.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#drift' || !drift?.has_drift) return;
    const el = document.getElementById('drift');
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.add('ring-2', 'ring-amber-400');
    const t = setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 2000);
    return () => clearTimeout(t);
  }, [drift]);

  const overrides = event?.[overridesKey] || {};
  // Staged-but-unpushed keys (server-tracked, survives reloads).
  const pending = live?.sync?.pending_push || [];
  const overrideCount = Object.keys(overrides).length;

  // Group catalog by `group`, dropping fields whose UI another field owns
  // (cover/additional photos → the images editor; individualAttractions →
  // the attractions editor).
  const groups = useMemo(() => {
    const g = {};
    for (const f of fieldCatalog) {
      if (FIELD_RENDERED_ELSEWHERE.has(f.key)) continue;
      (g[f.group] = g[f.group] || []).push(f);
    }
    return g;
  }, [fieldCatalog]);

  // Source for a catalog field: pinned ('event') if it (or, for the images
  // editor, any of its sibling keys) has an override key; else the resolver's
  // per-field provenance.
  const sourceFor = (key) => {
    if (overrides[key] !== undefined) return 'event';
    return resolved?.sources?.[sourceKey(key)] || 'default';
  };

  // The live value for a catalog key, in the /live response's vocabulary —
  // this is the collision-check baseline (what we loaded/edited from).
  const liveBaselineFor = (key) => {
    if (!live) return undefined;
    if (key === 'rateLadder') return live.rates;
    if (key === 'hostFields') return live.hostFields;
    if (key === 'eventPhoto' || key === 'coverPhoto' || key === 'additionalPhotos') return live.images?.[key];
    return live.scalars?.[key];
  };

  // Persist one override key. Skips pinning a not-yet-overridden field that's
  // being set to a value equal to what it already inherits.
  // With pushOnSave: STAGES — saves the local override and records the key as
  // pending; nothing reaches the platform until an explicit push (the pending
  // banner's "Push all" or a drift row's "Push →"). Collision checks moved to
  // push time.
  const patch = async (key, value, { force = false } = {}) => {
    if (busy) return;
    if (!force && value !== null && overrides[key] === undefined && jsonEq(inheritedValue(resolved, key), value)) {
      return; // no real change, don't create a redundant override
    }
    setBusy(true); setError(null); setPushNote(null);
    try {
      if (pushOnSave) {
        const r = await apiFetch(`/api/tenants/${tenantSlug}/${platform}/events/${eventId}/save-field?authorize_writes=1`, {
          method: 'POST',
          body: JSON.stringify({ key, value }),
        });
        const d = await r.json();
        if (!r.ok && !d.saved) throw new Error(d.detail?.toString?.() || d.error || 'save_failed');
        setPushNote(d.staged
          ? { key, status: 'staged' }
          : d.inSync
            ? { key, status: 'insync' }
            : { key, status: 'local', detail: typeof d.detail === 'string' ? d.detail : (d.reason || '') });
      } else {
        const r = await apiFetch(`/api/tenants/${tenantSlug}/${platform}/events/${eventId}/overrides`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ [key]: value }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || d.error || 'patch_failed');
      }
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  // Save a field's value up an inheritance tier (venue defaults / residency
  // template) so future events resolve it — per-field, intentional seeding.
  const promoteField = async (key, scope, value) => {
    if (busy) return;
    const scopeLabel = scope === 'venue'
      ? 'venue defaults; this applies to ALL events at this venue'
      : 'residency template, this applies to all events using it';
    const preview = typeof value === 'object' ? JSON.stringify(value).slice(0, 120) : String(value);
    if (!window.confirm(`Save ${key} = "${preview}" to the ${scopeLabel}. Continue?`)) return;
    setBusy(true); setError(null); setPushNote(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/${platform}/events/${eventId}/promote-field?authorize_writes=1`, {
        method: 'POST',
        body: JSON.stringify({ key, scope, value }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'promote_failed');
      setPushNote({ key, status: 'promoted', detail: scope === 'venue' ? 'venue defaults' : 'template' });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  // refreshLive: also re-pull /live so the editor's live lines / Overview pick
  // up concurrent Universe-portal edits mid-session (manual checks only — the
  // auto-check on load runs right after load() already fetched live).
  // UrVenue: apply tier-level changes for the event date. BATCHED — the panel
  // accumulates +/- clicks locally and nothing reaches UrVenue until the
  // explicit Apply (mirrors UrVenue's own Day Settings Save button and our
  // stage-then-push convention). One reload after the batch.
  const applyTableTiers = async (changes) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      for (const [masteritemid, tier] of Object.entries(changes)) {
        const r = await apiFetch(`/api/tenants/${tenantSlug}/${platform}/events/${eventId}/table-tier?authorize_writes=1`, {
          method: 'POST', body: JSON.stringify({ masteritemid, tier }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(`${masteritemid}: ${d.detail || d.error || 'tier_failed'}`);
      }
      setPushNote({ key: `${Object.keys(changes).length} tier change${Object.keys(changes).length === 1 ? '' : 's'}`, status: 'pushed' });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const checkDrift = async (refreshLive = false) => {
    setDriftLoading(true); setError(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/${platform}/events/${eventId}/drift`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'drift_failed');
      setDrift(d);
      if (refreshLive) {
        const lv = await apiFetch(`/api/tenants/${tenantSlug}/${platform}/events/${eventId}/live`)
          .then((r2) => (r2.ok ? r2.json() : null)).catch(() => null);
        if (lv && lv.published) setLive(lv);
      }
    } catch (e) { setError(e.message); }
    finally { setDriftLoading(false); }
  };

  // Explicit push of one key: re-asserts our current resolved value on the
  // platform (save-field pushOnly — no override write, inheritance untouched).
  // Collision-checked against the live baseline we loaded unless forced; a
  // 409 opens the conflict panel. Returns true on success.
  const pushKey = async (key, { force = false } = {}) => {
    setError(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/${platform}/events/${eventId}/save-field?authorize_writes=1`, {
        method: 'POST',
        body: JSON.stringify({ key, pushOnly: true, force, ...(force ? {} : { baseline: liveBaselineFor(key) }) }),
      });
      const d = await r.json();
      if (r.status === 409 && d.conflict) {
        setConflict({ key, ours: inheritedValue(resolved, key), live: d.conflict.live });
        return false;
      }
      if (!r.ok) throw new Error(d.detail?.toString?.() || d.error || 'push_failed');
      return true;
    } catch (e) { setError(e.message); return false; }
  };
  const pushOurs = async (key) => {
    if (busy) return;
    setBusy(true);
    try {
      const ok = await pushKey(key, { force: true });
      if (ok) { setPushNote({ key, status: 'pushed' }); await load(); await checkDrift(true); }
    } finally { setBusy(false); }
  };
  // Push every staged key in order; stops on the first conflict/error so the
  // dialog (or error line) is visible before the rest go out.
  const pushAll = async () => {
    if (busy) return;
    setBusy(true); setPushNote(null);
    try {
      let pushedCount = 0;
      for (const key of pending) {
        const ok = await pushKey(key);
        if (!ok) break;
        pushedCount += 1;
      }
      if (pushedCount) setPushNote({ key: `${pushedCount} field${pushedCount === 1 ? '' : 's'}`, status: 'pushed' });
      await load(); await checkDrift(true);
    } finally { setBusy(false); }
  };
  // ← Accept makes our config agree with live: clears the event override when
  // the fallback already matches live, else pins live as the override.
  const acceptLive = async (key) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/${platform}/events/${eventId}/accept-live?authorize_writes=1`, {
        method: 'POST', body: JSON.stringify({ key }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'accept_failed');
      await load(); await checkDrift(true);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (error && !resolved) {
    return (
      <div className="max-w-full md:max-w-4xl">
        <BreadcrumbHeader tenantSlug={tenantSlug} eventId={eventId} event={event} platformLabel={platformLabel} />
        <div className="text-destructive text-sm mt-4">{error}</div>
        <button onClick={load} className="mt-2 px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">Retry</button>
      </div>
    );
  }
  if (!resolved || !event) {
    return (
      <div className="max-w-full md:max-w-4xl">
        <LoadingState label={`Loading ${platformLabel} listing…`} />
      </div>
    );
  }

  const ctx = { tenantSlug, platform, eventId };

  // Tabs from the catalog groups. Use the platform's explicit groupOrder when
  // supplied (Universe mirrors its real editor section order); else Tickets-first.
  const groupNames = Object.keys(groups);
  const orderedGroups = groupOrder
    ? [
        ...groupOrder.filter((g) => groupNames.includes(g)),
        ...groupNames.filter((g) => !groupOrder.includes(g)),
      ]
    : [
        ...groupNames.filter((g) => /ticket/i.test(g)),
        ...groupNames.filter((g) => !/ticket/i.test(g)),
      ];
  if (overviewTab) orderedGroups.unshift('Overview');
  const tab = activeTab && (groups[activeTab] || activeTab === 'Overview') ? activeTab : orderedGroups[0];

  // Live status strip data.
  const totalSold = (live?.rates || []).reduce((s, r) => s + (r.sold || 0), 0);
  const liveUrl = live?.url || (event.publications || []).find((p) => p.platform === platform)?.external_url;

  return (
    <div className="max-w-full md:max-w-4xl">
      <BreadcrumbHeader tenantSlug={tenantSlug} eventId={eventId} event={event} platformLabel={platformLabel} />

      {/* Live status strip, the listing's reality at a glance */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs border border-border rounded-md px-3 py-2 bg-muted/20">
        {live ? (
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            {live.state === 'posted' || live.state === 'published' ? 'Published' : (live.state || 'Live')}
          </span>
        ) : (
          <span className="text-muted-foreground">Not published</span>
        )}
        {live && <span className="text-muted-foreground">{totalSold.toLocaleString()} sold</span>}
        {drift && (
          <span className={drift.has_drift ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}>
            {drift.has_drift ? `◆ ${drift.deltas.length} drifted` : '○ in sync'}
          </span>
        )}
        <span className="text-muted-foreground">
          <span className="font-medium text-foreground">{overrideCount}</span> field{overrideCount === 1 ? '' : 's'} customized
        </span>
        <span className="flex-1" />
        {liveUrl && <a href={liveUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">Open on {platformLabel} ↗</a>}
        <button onClick={() => checkDrift(true)} disabled={driftLoading} className="px-2 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50">{driftLoading ? 'Checking…' : 'Check drift'}</button>
      </div>

      {error && <div className="text-xs text-destructive mt-2">{error}</div>}

      {/* Save→push result (Universe save-field path) */}
      {pushNote && (
        <div className={`text-xs mt-2 px-3 py-1.5 rounded border ${
          pushNote.status === 'pushed' || pushNote.status === 'promoted' || pushNote.status === 'insync' ? 'border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
          : pushNote.status === 'failed' ? 'border-destructive/40 text-destructive bg-destructive/5'
          : 'border-border text-muted-foreground bg-muted/20'
        }`}>
          {pushNote.status === 'pushed' && <>✓ <span className="font-medium">{pushNote.key}</span> pushed to {platformLabel}.</>}
          {pushNote.status === 'staged' && <><span className="font-medium">{pushNote.key}</span> saved locally, staged for push.</>}
          {pushNote.status === 'insync' && <>✓ <span className="font-medium">{pushNote.key}</span> saved, matches {platformLabel}, nothing to push.</>}
          {pushNote.status === 'promoted' && <>✓ <span className="font-medium">{pushNote.key}</span> saved to the {pushNote.detail}.</>}
          {pushNote.status === 'local' && <><span className="font-medium">{pushNote.key}</span> saved locally{pushNote.detail ? `, ${pushNote.detail}` : ''} (not pushed).</>}
          {pushNote.status === 'failed' && <>✗ <span className="font-medium">{pushNote.key}</span> saved locally but the {platformLabel} push failed{pushNote.detail ? `: ${pushNote.detail}` : ''}.</>}
        </div>
      )}

      {/* Staged edits not yet on the platform, explicit push model */}
      {pending.length > 0 && (
        <div className="mt-2 px-3 py-2 rounded border border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-xs flex items-center gap-3 flex-wrap">
          <span className="text-amber-800 dark:text-amber-300">
            ⚠ {pending.length} edit{pending.length === 1 ? '' : 's'} staged locally, not yet on {platformLabel}:{' '}
            <span className="font-mono">{pending.join(', ')}</span>
          </span>
          <span className="flex-1" />
          <button onClick={pushAll} disabled={busy}
            className="px-2.5 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
            {busy ? 'Pushing…' : `Push all (${pending.length})`}
          </button>
        </div>
      )}

      {/* Collision: someone changed this field on the platform since we loaded */}
      {conflict && (
        <div className="mt-2 border border-amber-400 dark:border-amber-700 rounded-md p-3 bg-amber-50 dark:bg-amber-900/20 text-xs">
          <div className="font-medium text-amber-800 dark:text-amber-300 mb-2">
            ⚠ “{conflict.key}” changed on {platformLabel} since you loaded this page.
          </div>
          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Now live on {platformLabel}</div>
              <pre className="border border-border rounded bg-background p-2 max-h-40 overflow-auto whitespace-pre-wrap break-all">{JSON.stringify(conflict.live, null, 1)}</pre>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Your edit</div>
              <pre className="border border-border rounded bg-background p-2 max-h-40 overflow-auto whitespace-pre-wrap break-all">{JSON.stringify(conflict.ours, null, 1)}</pre>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={async () => { setBusy(true); try { const ok = await pushKey(conflict.key, { force: true }); if (ok) { setConflict(null); await load(); await checkDrift(true); } } finally { setBusy(false); } }} disabled={busy}
              className="px-2.5 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">Overwrite with mine</button>
            <button onClick={async () => { setConflict(null); await acceptLive(conflict.key); }} disabled={busy}
              className="px-2.5 py-1 rounded border border-input hover:bg-accent disabled:opacity-50">Take theirs</button>
            <button onClick={() => setConflict(null)} disabled={busy}
              className="px-2.5 py-1 rounded border border-input hover:bg-accent disabled:opacity-50">Cancel</button>
          </div>
        </div>
      )}

      {/* Drift resolve panel, surfaced at the TOP (not buried below the field
          tabs) whenever the listing diverges from our config, on every tab, so
          ops can Push/Accept immediately. Deep-linked via …/universe#drift. */}
      {drift?.has_drift && (
        <div id="drift" className="rounded-md transition-shadow">
          <DriftCompare
            drift={drift} platformLabel={platformLabel} busy={busy}
            onPush={pushOurs} onAccept={acceptLive}
            labelFor={(k) => fieldCatalog.find((f) => f.key === k)?.label || k}
            venueTz={event?.venue_timezone}
          />
        </div>
      )}

      {/* Section tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border mt-4 mb-3">
        {orderedGroups.map((g) => (
          <button
            key={g}
            onClick={() => setActiveTab(g)}
            className={`px-3 py-1.5 text-xs border-b-2 -mb-px ${tab === g ? 'border-primary text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >{g}</button>
        ))}
      </div>

      {/* Legend on the Tickets tab */}
      {/ticket/i.test(tab) && (
        <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground mb-2">
          <span><span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">Selling now</span> open</span>
          <span><span className="px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-700 dark:text-amber-400">Queued</span> opens after another sells out</span>
          <span><span className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-700 dark:text-red-300">SOLD OUT</span></span>
          <span><span className="px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-700 dark:text-sky-300">N left</span> remaining</span>
        </div>
      )}

      {tab !== 'Overview' && <SourceLegend className="mb-2" />}

      {/* Active group's fields (or the Overview panel) */}
      {tab === 'Overview' ? (
        <OverviewPanel
          ratesTitle={platform === 'urvenue' ? 'Live tables (booked / total)' : 'Live rate ladder'}
          soldNoun={platform === 'urvenue' ? 'booked' : 'sold'}
          onTierApply={platform === 'urvenue' ? applyTableTiers : null}
          busy={busy}
          live={live} drift={drift} totalSold={totalSold} liveUrl={liveUrl}
          overrides={overrides} overrideCount={overrideCount} platformLabel={platformLabel}
          onJump={(g) => setActiveTab(g)} availableGroups={orderedGroups}
        />
      ) : (
        <>
          {/* fields, sub-grouped by `section` when present */}
          <div className="border border-border rounded-md px-3 py-1.5">
            {groupBySections(groups[tab] || []).map(({ section, items }) => (
              <div key={section || '_'}>
                {section && (
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mt-3 mb-1 first:mt-1.5">{section}</div>
                )}
                {items.map((meta) => (
                  <OverrideField
                    key={meta.key}
                    meta={meta}
                    value={inheritedValue(resolved, meta.key)}
                    source={sourceFor(meta.key)}
                    resolved={resolved}
                    sources={resolved.sources || {}}
                    event={event}
                    live={live}
                    ctx={ctx}
                    patch={patch}
                    busy={busy}
                    onPromote={promoteFields ? promoteField : null}
                    staged={pending.includes(meta.key)}
                    promotable={promotable}
                  />
                ))}
              </div>
            ))}
          </div>
          {/* (Drift resolve panel now renders at the top of the page, above the
              tabs, see the `#drift` block, so it isn't buried here.) */}
        </>
      )}

      <div className="mt-6">
        <button
          onClick={() => setShowFullPayload((v) => !v)}
          aria-expanded={showFullPayload}
          className="text-xs text-muted-foreground hover:text-foreground"
        >{showFullPayload ? '▲ Hide' : '▼ Show'} full live payload (read-only)</button>
        {showFullPayload && (
          <pre className="mt-2 p-3 bg-muted/30 rounded text-[10px] overflow-x-auto max-h-[500px] overflow-y-auto">
            {JSON.stringify(resolved.payload, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

// Overview tab — the landing view: status + live rate snapshot + customized
// fields + quick links. All from data the shell already loaded.
function OverviewPanel({ live, drift, totalSold, liveUrl, overrides, overrideCount, platformLabel, onJump, availableGroups, ratesTitle = 'Live rate ladder', soldNoun = 'sold', onTierApply = null, busy = false }) {
  // Tier +/- accumulates locally; nothing fires until Apply.
  const [tierDraft, setTierDraft] = useState({});
  useEffect(() => { setTierDraft({}); }, [live]);
  const tierChanges = Object.fromEntries(Object.entries(tierDraft).filter(([id, t]) => {
    const r = (live?.rates || []).find((x) => String(x.id) === String(id));
    return r && Number(t) !== Number(r.tier ?? 0);
  }));
  const tierDirty = Object.keys(tierChanges).length;
  const rates = live?.rates || [];
  const pushedAgo = timeAgo(live?.sync?.last_pushed_at);
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="border border-border rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Status</div>
          <div className="text-lg font-semibold mt-0.5">{live ? (live.state === 'posted' || live.state === 'published' ? 'Published' : (live.state || 'Live')) : 'Not published'}</div>
          {pushedAgo && <div className="text-[11px] text-muted-foreground mt-0.5">Pushed {pushedAgo}</div>}
        </div>
        {rates.length > 0 && (
          <div className="border border-border rounded-md p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Sold</div>
            <div className="text-lg font-semibold mt-0.5">{totalSold.toLocaleString()}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">across {rates.length} tier{rates.length === 1 ? '' : 's'}</div>
          </div>
        )}
        <div className="border border-border rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Sync</div>
          <div className={`text-lg font-semibold mt-0.5 ${drift?.has_drift ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
            {drift ? (drift.has_drift ? `${drift.deltas.length} drifted` : 'In sync') : 'Checking…'}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{overrideCount} field{overrideCount === 1 ? '' : 's'} customized</div>
        </div>
      </div>

      {rates.length > 0 && (
        <div className="border border-border rounded-md overflow-x-auto">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-3 py-1.5 bg-muted/30 flex items-center justify-between">
            <span>{ratesTitle}</span>
            <button onClick={() => onJump('Tickets')} className="text-primary hover:underline normal-case tracking-normal">Edit tickets →</button>
          </div>
          <table className="w-full text-xs">
            <tbody>
              {rates.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-1.5">{r.name}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {(() => {
                      // While a tier change is staged, preview the resulting price.
                      const draftTier = onTierApply ? (tierDraft[r.id] ?? (r.tier ?? 0)) : (r.tier ?? 0);
                      const preview = r.tierStep != null && draftTier !== (r.tier ?? 0)
                        ? (r.basePrice ?? r.price) + draftTier * r.tierStep
                        : r.price;
                      // $0 rates show as "Free" (matches the $0→FREE_TICKET rule).
                      const money = (p) => (Number(p) > 0 ? `$${Number(p).toLocaleString()}` : 'Free');
                      return preview !== r.price
                        ? <span className="text-amber-600 dark:text-amber-400">{money(preview)} <span className="text-[10px]">(was {money(r.price)})</span></span>
                        : <>{money(r.price)}</>;
                    })()}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{r.sold}{r.remaining != null ? ` / ${r.sold + r.remaining}` : ''} {soldNoun}</td>
                  {onTierApply && (() => {
                    const cur = tierDraft[r.id] ?? (r.tier ?? 0);
                    const changed = Number(cur) !== Number(r.tier ?? 0);
                    return (
                      <td className="px-3 py-1.5 text-right whitespace-nowrap">
                        <span className="inline-flex items-center gap-1" title="Tier level, raises the price per the yielding rules. Changes apply on the Apply button.">
                          <button onClick={() => !busy && setTierDraft((d) => { const c = d[r.id] ?? (r.tier ?? 0); return c > 0 ? { ...d, [r.id]: c - 1 } : d; })} disabled={busy || cur <= 0}
                            className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-30">−</button>
                          <span className={`text-[11px] tabular-nums w-7 text-center ${changed ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}`}>T{cur}</span>
                          <button onClick={() => !busy && setTierDraft((d) => ({ ...d, [r.id]: (d[r.id] ?? (r.tier ?? 0)) + 1 }))} disabled={busy}
                            className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-30">+</button>
                        </span>
                      </td>
                    );
                  })()}
                  <td className="px-3 py-1.5 text-right">
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                        r.liveStatus === 'sold_out' ? 'bg-red-500/15 text-red-700 dark:text-red-300'
                        : r.liveStatus === 'selling' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                        : r.liveStatus === 'queued' ? 'bg-amber-400/15 text-amber-700 dark:text-amber-400'
                        : 'bg-muted text-muted-foreground'}`}
                      title={r.liveStatus === 'queued' && r.opensAfter ? `Opens when “${r.opensAfter}” sells out` : undefined}
                    >{r.liveStatus === 'queued' ? 'tiered' : r.liveStatus}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {onTierApply && tierDirty > 0 && (
            <div className="flex items-center gap-3 px-3 py-2 border-t border-amber-400/50 bg-amber-50 dark:bg-amber-900/20 text-xs">
              <span className="text-amber-800 dark:text-amber-300">⚠ {tierDirty} tier change{tierDirty === 1 ? '' : 's'} not yet applied to {platformLabel}</span>
              <span className="flex-1" />
              <button onClick={() => setTierDraft({})} disabled={busy}
                className="px-2 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50">Cancel</button>
              <button onClick={() => onTierApply(tierChanges)} disabled={busy}
                className="px-2.5 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
                {busy ? 'Applying…' : `Apply (${tierDirty})`}
              </button>
            </div>
          )}
        </div>
      )}

      {overrideCount > 0 && (
        <div className="border border-border rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Customized for this event</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(overrides).map((k) => (
              <span key={k} className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300">{k}</span>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

function BreadcrumbHeader({ tenantSlug, eventId, event, platformLabel }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">
        <Link to={`/${tenantSlug}/calendar`} className="hover:underline">events</Link>
        {' / '}
        <Link to={`/${tenantSlug}/event/${eventId}`} className="hover:underline">
          {event?.talent || 'event'}
        </Link>
        {' / '}
        <span>{platformLabel}</span>
      </div>
      <h1 className="text-xl font-semibold">{platformLabel}</h1>
      {event && <div className="text-sm text-muted-foreground">{event.talent} · {formatDateLong(event.date)}</div>}
    </div>
  );
}

function DriftCompare({ drift, platformLabel, busy, onPush, onAccept, labelFor, venueTz }) {
  const [field, setField] = useState(null); // which row is processing
  const run = async (fn, k) => { setField(k); try { await fn(k); } finally { setField(null); } };
  return (
    <div className="border border-border rounded-md mt-4 p-3 text-xs">
      <div className="flex items-center justify-between mb-1">
        <div className="font-medium">
          {drift.has_drift ? `◆ ${drift.deltas.length} field(s) differ from ${platformLabel}` : `○ In sync with ${platformLabel}`}
        </div>
        <div className="text-muted-foreground">{drift.listing_id}</div>
      </div>
      {/* The "something changed" indicator, the listing's own modification
          stamp. No comparison against our writes: the platform is the source
          of truth and portal edits are normal workflow. */}
      {drift.listing_modified_at && (
        <div className="text-[10px] text-muted-foreground mb-2">
          Listing last modified on {platformLabel}: <span className="text-foreground/80">{formatStamp(drift.listing_modified_at, venueTz)}</span>
        </div>
      )}
      {drift.deltas?.length > 0 && (
        <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1">Field</th>
              <th className="py-1">Ours (current config)</th>
              <th className="py-1">{platformLabel} (live)</th>
              <th className="py-1 text-right">Resolve</th>
            </tr>
          </thead>
          <tbody>
            {drift.deltas.map((d) => {
              const rowBusy = field === d.key;
              return (
                <tr key={d.key} className="border-t border-border align-top">
                  <td className="py-1 pr-2">
                    <div>{labelFor ? labelFor(d.key) : d.key}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{d.key}</div>
                  </td>
                  <td className="py-1 pr-2 break-all">{JSON.stringify(d.ours)}</td>
                  <td className="py-1 pr-2 break-all">{JSON.stringify(d.live)}</td>
                  <td className="py-1 text-right whitespace-nowrap">
                    <button
                      onClick={() => run(onPush, d.key)}
                      disabled={busy}
                      aria-disabled={busy}
                      className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50 mr-1"
                      title={`Re-assert our value on ${platformLabel} (overwrites the portal edit)`}
                    >{rowBusy ? 'pushing…' : 'Push →'}</button>
                    <button
                      onClick={() => run(onAccept, d.key)}
                      disabled={busy}
                      aria-disabled={busy}
                      className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50"
                      title={`Make our config agree with the live ${platformLabel} value`}
                    >{rowBusy ? 'accepting…' : '← Accept'}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
      {!drift.deltas?.length && (
        <div className="text-[10px] text-muted-foreground">Our config matches the live listing on every field we manage.</div>
      )}
    </div>
  );
}
